import Axios, { InternalAxiosRequestConfig } from "axios";
import qs from "qs";
import { serviceOptions } from "@/api";
import { UserContext } from "@/context/UserContext";
import loggerClient from "./LoggerClient";
import { logOut } from "./store";

const axios = Axios.create({
  baseURL: window.origin.endsWith("amazonaws.com")
    ? "https://jywolaqbr3.execute-api.us-east-1.amazonaws.com"
    : undefined,
  paramsSerializer: {
    serialize: params => qs.stringify(params, { arrayFormat: "repeat" }),
  },
});

const PATHS_TO_EXCLUDE = ["/api/refreshtoken"];

type InternalAxiosRequestConfigWithMetaData = InternalAxiosRequestConfig & {
  metadata?: { startTime: number };
};

axios.interceptors.request.use(
  (config: InternalAxiosRequestConfigWithMetaData) => {
    // The generated IRequestOptions.withAuthorization flag is not interpreted
    // here. TLX session headers follow PATHS_TO_EXCLUDE and caller headers below;
    // withAuthorization: false alone does not make a request unauthenticated.
    if (!PATHS_TO_EXCLUDE.includes(config.url as string)) {
      const user = UserContext.getUser();
      const token = UserContext.getToken();
      // Only inject values that actually exist, and never overwrite a header
      // the caller supplied. `getUser()` returns `{}` (truthy) when no user is
      // stored yet, so a blanket assignment would set `tenantId: undefined`
      // during login and silently drop the caller's own tenantId.
      // `has()` on AxiosHeaders is case-insensitive, so a caller passing
      // `authorization`/`tenantid` is respected too.
      if (token && !config.headers.has("Authorization")) {
        config.headers.set("Authorization", `Bearer ${token}`);
      }
      if (user?.tenant && !config.headers.has("tenantId")) {
        config.headers.set("tenantId", user.tenant);
      }
    }

    config.metadata = { startTime: Date.now() };

    return config;
  },
  error => Promise.reject(error),
);

axios.interceptors.response.use(
  response => {
    const { config, status, request } = response;
    const endTime = Date.now();
    const startTime =
      (config as InternalAxiosRequestConfigWithMetaData)?.metadata?.startTime ||
      Date.now();
    const responseTime = endTime - startTime;

    loggerClient.recordEvent("ServiceCall.duration", {
      message: `API: ${config.method} -> ${request.responseURL} took ${responseTime}ms`,
      duration: String(responseTime),
    });

    loggerClient.recordEvent(`ServiceCall.${status}`, {
      message: `Service Called  status: ${status} URL: ${request.responseURL}  Method: ${config.method}`,
    });

    return response;
  },
  error => {
    const {
      response: { status, data },
      request,
      config: { url, method },
    } = error;

    const endTime = Date.now();
    const startTime =
      (error.config as InternalAxiosRequestConfigWithMetaData)?.metadata
        ?.startTime || Date.now();
    const responseTime = endTime - startTime;

    loggerClient.recordEvent("ServiceCall.duration", {
      message: `API: ${method} -> ${request.responseURL} failed after ${responseTime}ms`,
      duration: String(responseTime),
    });

    loggerClient.recordEvent(`ServiceCall.${status}`, {
      message: `API failed with status : ${request.status} URL: ${request.responseURL}  Method: ${method}`,
      body: data,
    });

    if (
      status === 401 &&
      url !== "/api/login" &&
      url !== "/api/logout" &&
      url !== "/api/user/changePassword"
    ) {
      if (request) {
        // eslint-disable-next-line no-console
        console.log(
          `API failed with 401 :: status : ${request.status} :: response : ${request.response} :: responseURL : ${request.responseURL}`,
        );
      }
      return logOut();
    }
    return Promise.reject(error);
  },
);

serviceOptions.axios = axios;
export default axios;
